// Phone layout of an MIS report: one compact card per record, same fields as the table.
// Tapping a card opens its record; the View button inside is the keyboard and screen-reader route.
import { Box, Pagination, Skeleton, Stack, Typography } from "@mui/material";
import { DASHBOARD_COLORS as C } from "../DashboardCard";
import { REPORTS } from "./reportConfig";
import { ReportCell } from "./ReportCells";

export default function ReportCardsMobile({ tab, rows = [], loading, page = 0, pageSize = 10, onPageChange, renderAction, onOpen, emptyState }) {
  if (loading) {
    return (
      <Stack spacing={1} sx={{ p: 1.25 }} aria-label="Loading records">
        {[0, 1, 2].map((key) => (
          <Skeleton key={key} variant="rounded" height={104} sx={{ borderRadius: "10px" }} />
        ))}
      </Stack>
    );
  }
  if (!rows.length) return emptyState;

  const report = REPORTS[tab];
  const primary = report.columns.find((c) => c.primary);
  const details = report.columns.filter((c) => !c.primary);
  const pageCount = Math.ceil(rows.length / pageSize);
  const visible = rows.slice(page * pageSize, (page + 1) * pageSize);

  return (
    <Box sx={{ p: 1.25 }}>
      <Stack spacing={1} component="ul" aria-label={`${report.title} records`} sx={{ listStyle: "none", m: 0, p: 0 }}>
        {visible.map((row, index) => (
          <Box
            component="li"
            key={row.__key ?? index}
            onClick={onOpen ? () => onOpen(row) : undefined}
            sx={{
              border: `1px solid ${C.border}`,
              borderRadius: "10px",
              p: 1.25,
              bgcolor: "#fff",
              minWidth: 0,
              ...(onOpen && { cursor: "pointer", "&:hover, &:active": { bgcolor: C.primarySoft, borderColor: "#BFDBFE" } }),
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, minWidth: 0 }}>
              <Typography component="h2" sx={{ fontSize: 15, fontWeight: 600, color: C.navy, lineHeight: 1.3, overflowWrap: "anywhere", minWidth: 0 }}>
                {row[primary.field] || "—"}
              </Typography>
              {renderAction?.(row)}
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${details.length > 4 ? 2 : Math.max(details.length, 1)}, minmax(0, 1fr))`, gap: 1, mt: 1 }}>
              {details.map((column) => (
                <Box key={column.field} sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 11.5, color: C.muted, lineHeight: 1.3, mb: 0.25 }}>{column.header}</Typography>
                  <ReportCell column={column} value={row[column.field]} />
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Stack>
      {pageCount > 1 && (
        <Stack alignItems="center" mt={1.5}>
          <Pagination
            count={pageCount}
            page={page + 1}
            onChange={(e, value) => onPageChange?.(value - 1)}
            color="primary"
            size="small"
            siblingCount={0}
            showFirstButton
            showLastButton
            aria-label="Report pages"
          />
        </Stack>
      )}
    </Box>
  );
}
